import {
  BarChart,
  Calendar,
  ChevronDown,
  DollarSign,
  Eye,
  FileText,
  FolderOpen,
  Globe,
  Hash,
  MapPin,
  MousePointer,
  Percent,
  Target,
  TrendingUp,
} from 'lucide-react'
import { useState } from 'react'
import type { NodeExecution } from 'sprites-types/api/v2/workflows'
import { getToolResultSchema } from 'sprites-types/workflow/tools/common'
import {
  googleAdsGetCampaignDetails,
  type GoogleAdsGetCampaignDetailsResult,
} from 'sprites-types/workflow/tools/googleAdsGetCampaignDetails'
import { ContextMenuWrapper } from '../shared/ContextMenuWrapper'
import { ObjectDisplay } from '../shared/ObjectDisplay'
import {
  createWorkflowToolComponent,
  type WorkflowToolComponentProps,
} from '../shared/WorkflowToolComponent'
import { SchemaValidationError } from '../shared/safeParseToolResult'

type CampaignPerformanceItem = GoogleAdsGetCampaignDetailsResult[number]
type CampaignPerformanceResult = GoogleAdsGetCampaignDetailsResult | CampaignPerformanceItem

// Utility functions
const formatCurrency = (amount: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount)
}

const formatNumber = (num: number) => {
  if (num >= 1000000) return `${(num / 1000000).toFixed(1)}M`
  if (num >= 1000) return `${(num / 1000).toFixed(1)}K`
  return num.toLocaleString()
}

const getStatusColor = (status: string | null | undefined) => {
  switch (status) {
    case 'ENABLED':
      return 'bg-green-100 text-green-800'
    case 'PAUSED':
      return 'bg-yellow-100 text-yellow-800'
    case 'REMOVED':
      return 'bg-red-100 text-red-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

const getCampaignTypeColor = (type: string | null | undefined) => {
  switch (type) {
    case 'SEARCH':
      return 'bg-blue-100 text-blue-800'
    case 'DISPLAY':
      return 'bg-purple-100 text-purple-800'
    case 'VIDEO':
      return 'bg-red-100 text-red-800'
    case 'SHOPPING':
      return 'bg-orange-100 text-orange-800'
    case 'PERFORMANCE_MAX':
      return 'bg-indigo-100 text-indigo-800'
    default:
      return 'bg-gray-100 text-gray-800'
  }
}

// Extract and calculate performance metrics
const extractPerformanceMetrics = (campaign: CampaignPerformanceItem) => {
  const performance = campaign.performance || {}
  const impressions = performance.impressions || 0
  const clicks = performance.clicks || 0
  const cost = performance.cost || 0
  const conversions = performance.conversions || 0
  const ctr = performance.ctr || (clicks > 0 && impressions > 0 ? (clicks / impressions) * 100 : 0)
  const cpc = performance.cpc || (clicks > 0 ? cost / clicks : 0)
  const cpm = performance.cpm || (impressions > 0 ? (cost / impressions) * 1000 : 0)
  const conversionRate =
    performance.conversionRate || (clicks > 0 && conversions > 0 ? (conversions / clicks) * 100 : 0)

  return { impressions, clicks, cost, conversions, ctr, cpc, cpm, conversionRate }
}

// Extract campaigns from result
const extractCampaigns = (result: CampaignPerformanceResult): CampaignPerformanceItem[] => {
  if (Array.isArray(result)) {
    return result.flatMap((item: CampaignPerformanceItem) => item)
  }
  return [result as CampaignPerformanceItem]
}

function CampaignItem({
  campaign,
  className,
}: {
  campaign: CampaignPerformanceItem
  className?: string
}) {
  const { impressions, clicks, cost, conversions, ctr, cpc, cpm, conversionRate } =
    extractPerformanceMetrics(campaign)

  return (
    <div
      className={`rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-sm ${className}`}
    >
      <div className="px-4 py-4">
        {/* Header Section */}
        <div className="mb-4 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <TrendingUp className="h-5 w-5 text-blue-500" />
            <div>
              <div className="text-base font-semibold text-gray-900">
                {campaign.campaignName || 'Campaign'}
              </div>
              <div className="text-xs text-gray-600">ID: {campaign.campaignId}</div>
            </div>
          </div>
          <div className="flex flex-col items-end gap-2">
            <div className="flex items-center gap-2">
              {campaign.campaignType && (
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${getCampaignTypeColor(campaign.campaignType)}`}
                >
                  {campaign.campaignType}
                </span>
              )}
              {campaign.status && (
                <span
                  className={`rounded-full px-2 py-1 text-xs font-medium ${getStatusColor(campaign.status)}`}
                >
                  {campaign.status}
                </span>
              )}
            </div>
            {campaign.budget && (
              <div className="text-xs text-gray-600">
                {formatCurrency(campaign.budget.amount || 0)}/
                {campaign.budget.type?.toLowerCase() || 'day'}
              </div>
            )}
          </div>
        </div>

        {/* Campaign Details */}
        {(campaign.biddingStrategy || campaign.servingStatus || campaign.startDate) && (
          <div className="mb-4 rounded-lg bg-gray-50/50 p-3">
            <div className="grid grid-cols-2 gap-3 text-xs">
              {campaign.biddingStrategy && (
                <div className="flex items-center gap-2">
                  <Target className="h-3 w-3 text-indigo-500" />
                  <div>
                    <div className="font-medium text-gray-900">{campaign.biddingStrategy}</div>
                    <div className="text-gray-600">Bidding Strategy</div>
                  </div>
                </div>
              )}
              {campaign.servingStatus && (
                <div className="flex items-center gap-2">
                  <BarChart className="h-3 w-3 text-green-500" />
                  <div>
                    <div className="font-medium text-gray-900">{campaign.servingStatus}</div>
                    <div className="text-gray-600">Serving Status</div>
                  </div>
                </div>
              )}
              {campaign.startDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3 text-blue-500" />
                  <div>
                    <div className="font-medium text-gray-900">{campaign.startDate}</div>
                    <div className="text-gray-600">Start Date</div>
                  </div>
                </div>
              )}
              {campaign.endDate && (
                <div className="flex items-center gap-2">
                  <Calendar className="h-3 w-3 text-red-500" />
                  <div>
                    <div className="font-medium text-gray-900">{campaign.endDate}</div>
                    <div className="text-gray-600">End Date</div>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Ad Groups and Ads Summary */}
        {(campaign.adGroups?.length || campaign.ads?.length) && (
          <div className="mb-4 rounded-lg bg-blue-50/50 p-3">
            <div className="mb-2 text-xs font-semibold text-gray-700">Campaign Structure</div>
            <div className="grid grid-cols-2 gap-3 text-xs">
              {campaign.adGroups && campaign.adGroups.length > 0 && (
                <div className="flex items-center gap-2">
                  <FolderOpen className="h-3 w-3 text-blue-500" />
                  <div>
                    <div className="font-medium text-gray-900">
                      {campaign.adGroups.length} Ad
                      {campaign.adGroups.length !== 1 ? ' Groups' : ' Group'}
                    </div>
                    <div className="text-gray-600">
                      {campaign.adGroups.filter((ag) => ag.status === 'ENABLED').length} enabled
                    </div>
                  </div>
                </div>
              )}
              {campaign.ads && campaign.ads.length > 0 && (
                <div className="flex items-center gap-2">
                  <FileText className="h-3 w-3 text-purple-500" />
                  <div>
                    <div className="font-medium text-gray-900">
                      {campaign.ads.length} Ad{campaign.ads.length !== 1 ? 's' : ''}
                    </div>
                    <div className="text-gray-600">
                      {campaign.ads.filter((ad) => ad.status === 'ENABLED').length} enabled
                    </div>
                  </div>
                </div>
              )}
            </div>
            {campaign.ads && campaign.ads.length > 0 && (
              <div className="mt-3 border-t border-blue-100 pt-2">
                <div className="mb-1 text-xs font-medium text-gray-700">Ad Types:</div>
                <div className="flex flex-wrap gap-1">
                  {Array.from(new Set(campaign.ads.map((ad) => ad.adType).filter(Boolean))).map(
                    (adType) => {
                      const count = campaign.ads?.filter((ad) => ad.adType === adType).length || 0
                      return (
                        <span
                          key={adType}
                          className="rounded-full bg-purple-100 px-2 py-0.5 text-xs font-medium text-purple-800"
                        >
                          {adType} ({count})
                        </span>
                      )
                    }
                  )}
                </div>
              </div>
            )}
          </div>
        )}

        {/* Performance Metrics */}
        <div className="mb-4">
          <div className="mb-2 text-sm font-medium text-gray-700">Performance Metrics</div>
          <div className="grid grid-cols-3 gap-3">
            {/* Primary Metrics */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 flex items-center gap-2">
                <Eye className="h-4 w-4 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Impressions</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{formatNumber(impressions)}</div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 flex items-center gap-2">
                <MousePointer className="h-4 w-4 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Clicks</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{formatNumber(clicks)}</div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 flex items-center gap-2">
                <DollarSign className="h-4 w-4 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">Cost</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{formatCurrency(cost)}</div>
            </div>

            {/* Secondary Metrics */}
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 flex items-center gap-2">
                <Percent className="h-4 w-4 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">CTR</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{ctr.toFixed(2)}%</div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 flex items-center gap-2">
                <Hash className="h-4 w-4 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">CPC</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{formatCurrency(cpc)}</div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <div className="mb-1 flex items-center gap-2">
                <Target className="h-4 w-4 text-gray-600" />
                <span className="text-xs font-medium text-gray-700">CPM</span>
              </div>
              <div className="text-lg font-bold text-gray-900">{formatCurrency(cpm)}</div>
            </div>
          </div>
        </div>

        {/* Conversions Section */}
        {conversions > 0 && (
          <div className="mb-4">
            <div className="mb-2 text-sm font-medium text-gray-700">Conversions</div>
            <div className="rounded-lg bg-emerald-50/70 p-3">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Target className="h-4 w-4 text-emerald-600" />
                  <span className="text-xs font-medium text-emerald-900">Total Conversions</span>
                </div>
                <div className="text-right">
                  <div className="text-lg font-bold text-emerald-900">{conversions}</div>
                  <div className="text-xs text-emerald-700">{conversionRate.toFixed(2)}% rate</div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Targeting Information */}
        {campaign.targeting &&
          (campaign.targeting.locations?.length ||
            campaign.targeting.languages?.length ||
            campaign.targeting.keywords?.length) && (
            <div className="mt-3 border-t border-gray-200 pt-2">
              <div className="space-y-1 text-xs">
                {campaign.targeting.locations && campaign.targeting.locations.length > 0 && (
                  <div className="flex items-center gap-1">
                    <MapPin className="h-3 w-3 text-blue-500" />
                    <span className="text-gray-600">Locations:</span>
                    <span className="text-gray-900">
                      {campaign.targeting.locations.slice(0, 3).join(', ')}
                    </span>
                    {campaign.targeting.locations.length > 3 && (
                      <span className="text-gray-500">
                        +{campaign.targeting.locations.length - 3} more
                      </span>
                    )}
                  </div>
                )}
                {campaign.targeting.languages && campaign.targeting.languages.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Globe className="h-3 w-3 text-purple-500" />
                    <span className="text-gray-600">Languages:</span>
                    <span className="text-gray-900">
                      {campaign.targeting.languages.slice(0, 3).join(', ')}
                    </span>
                    {campaign.targeting.languages.length > 3 && (
                      <span className="text-gray-500">
                        +{campaign.targeting.languages.length - 3} more
                      </span>
                    )}
                  </div>
                )}
                {campaign.targeting.keywords && campaign.targeting.keywords.length > 0 && (
                  <div className="flex items-center gap-1">
                    <Hash className="h-3 w-3 text-orange-500" />
                    <span className="text-gray-600">Keywords:</span>
                    <span className="text-gray-900">
                      {campaign.targeting.keywords.slice(0, 2).join(', ')}
                    </span>
                    {campaign.targeting.keywords.length > 2 && (
                      <span className="text-gray-500">
                        +{campaign.targeting.keywords.length - 2} more
                      </span>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}

        {/* Daily Breakdown */}
        {campaign.dailyBreakdown && campaign.dailyBreakdown.length > 0 && (
          <div className="mt-3 border-t border-gray-200 pt-2">
            <div className="mb-2 flex items-center gap-1">
              <Calendar className="h-3 w-3 text-indigo-500" />
              <span className="text-xs font-medium text-gray-700">Recent Performance</span>
            </div>
            <div className="space-y-1">
              {campaign.dailyBreakdown.slice(-3).map((day, index) => (
                <div key={day.date || index} className="flex justify-between text-xs">
                  <span className="text-gray-600">{day.date}</span>
                  <div className="flex gap-3 text-gray-900">
                    <span>{formatNumber(day.impressions || 0)} imp</span>
                    <span>{formatNumber(day.clicks || 0)} clicks</span>
                    <span>{formatCurrency(day.cost || 0)}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

function GoogleAdsGetCampaignDetailsDisplayComponent({
  nodeExecution,
}: WorkflowToolComponentProps) {
  const schema = getToolResultSchema(googleAdsGetCampaignDetails, nodeExecution.inputs)
  const [isExpanded, setIsExpanded] = useState(true)
  const [showJson, setShowJson] = useState(false)

  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return <SchemaValidationError error={parseResult.error} nodeExecution={nodeExecution} />
  }

  const result = parseResult.data as CampaignPerformanceResult

  const campaigns = extractCampaigns(result)

  const totalImpressions = campaigns.reduce(
    (sum: number, c: CampaignPerformanceItem) => sum + (c.performance?.impressions || 0),
    0
  )
  const totalClicks = campaigns.reduce(
    (sum: number, c: CampaignPerformanceItem) => sum + (c.performance?.clicks || 0),
    0
  )
  const totalCost = campaigns.reduce(
    (sum: number, c: CampaignPerformanceItem) => sum + (c.performance?.cost || 0),
    0
  )
  const totalConversions = campaigns.reduce(
    (sum: number, c: CampaignPerformanceItem) => sum + (c.performance?.conversions || 0),
    0
  )
  const averageCTR = totalImpressions > 0 ? (totalClicks / totalImpressions) * 100 : 0
  const averageCPC = totalClicks > 0 ? totalCost / totalClicks : 0
  const averageConversionRate = totalClicks > 0 ? (totalConversions / totalClicks) * 100 : 0

  const statusCounts = campaigns.reduce(
    (acc, c) => {
      const status = c.status || 'UNKNOWN'
      acc[status] = (acc[status] || 0) + 1
      return acc
    },
    {} as Record<string, number>
  )

  if (showJson) {
    return (
      <div className="rounded-lg border border-gray-200 bg-white p-4">
        <div className="mb-2 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-900">JSON View</h3>
          <button
            onClick={() => setShowJson(false)}
            className="text-xs text-blue-600 hover:text-blue-800"
          >
            Show Formatted View
          </button>
        </div>
        <ObjectDisplay data={nodeExecution.outputs} />
      </div>
    )
  }

  if (campaigns.length === 0) {
    return (
      <div className="rounded-3xl border border-gray-200/50 bg-white/70 text-black shadow-xl backdrop-blur-[8px]">
        <div className="flex items-center gap-4 px-4 py-3">
          <div className="flex-shrink-0">
            <TrendingUp className="h-5 w-5 text-gray-400" />
          </div>
          <div className="text-sm text-gray-600">No campaign performance data found</div>
        </div>
      </div>
    )
  }

  if (campaigns.length === 1) {
    return <CampaignItem campaign={campaigns[0]} />
  }

  return (
    <div className="space-y-3">
      <ContextMenuWrapper actions={[]}>
        <div className="overflow-hidden rounded-3xl border border-gray-200/50 bg-gray-200/70 text-black shadow-sm">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="group flex w-full items-center gap-4 bg-white px-4 py-3 text-left transition-colors hover:bg-gray-50/50"
            aria-expanded={isExpanded}
          >
            <div className="flex-shrink-0">
              <TrendingUp className="h-5 w-5 text-gray-600" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <div className="truncate text-sm font-semibold text-gray-900">
                  Google Ads Campaign Details
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    setShowJson(true)
                  }}
                  className="text-xs text-blue-600 hover:text-blue-800"
                >
                  View JSON
                </button>
              </div>
              <div className="space-y-0.5 text-xs font-medium tracking-wide text-gray-500">
                <div>
                  {campaigns.length} campaigns •{' '}
                  {Object.entries(statusCounts)
                    .map(([status, count]) => `${count} ${status.toLowerCase()}`)
                    .join(' • ')}
                </div>
                <div>
                  {totalImpressions.toLocaleString()} impressions • {totalClicks.toLocaleString()}{' '}
                  clicks • ${totalCost.toFixed(2)} spent
                </div>
                <div>
                  {averageCTR.toFixed(2)}% avg CTR • ${averageCPC.toFixed(2)} avg CPC
                  {totalConversions > 0 &&
                    ` • ${totalConversions} conversions (${averageConversionRate.toFixed(2)}%)`}
                </div>
              </div>
            </div>
            <div className="flex-shrink-0">
              <ChevronDown
                className={`h-4 w-4 text-gray-400 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`}
                aria-hidden="true"
              />
            </div>
          </button>
        </div>
      </ContextMenuWrapper>

      {isExpanded && (
        <div className="space-y-2">
          {campaigns.map((campaign: CampaignPerformanceItem, index: number) => (
            <CampaignItem
              key={campaign.campaignId || campaign.campaignName || index}
              campaign={campaign}
              className="mx-8"
            />
          ))}
        </div>
      )}
    </div>
  )
}

function downloadGoogleAdsCampaignPerformance(_nodeExecution: NodeExecution): Promise<void> {
  return Promise.resolve()
}

function googleAdsGetCampaignDetailsToMarkdown(nodeExecution: NodeExecution): string {
  const schema = getToolResultSchema(googleAdsGetCampaignDetails, nodeExecution.inputs)
  const parseResult = schema.safeParse(nodeExecution.outputs)

  if (!parseResult.success) {
    return '## Error: Schema validation failed\n\nUnable to export results due to data structure mismatch.'
  }

  const result = parseResult.data as CampaignPerformanceResult
  const campaigns = extractCampaigns(result)

  if (campaigns.length === 0) {
    return `## Google Ads Campaign Details

No campaign performance data found.

---
`
  }

  const campaignsMarkdown = campaigns
    .map((campaign: CampaignPerformanceItem) => {
      const { impressions, clicks, cost, conversions, ctr, cpc, cpm, conversionRate } =
        extractPerformanceMetrics(campaign)

      let campaignInfo = `### ${campaign.campaignName || 'Campaign'}`

      if (campaign.status) {
        campaignInfo += ` (${campaign.status})`
      }

      if (campaign.budget) {
        campaignInfo += `\n**Budget:** $${(campaign.budget.amount || 0).toFixed(2)}/${campaign.budget.type?.toLowerCase() || 'day'}`
      }

      if (campaign.adGroups?.length || campaign.ads?.length) {
        campaignInfo += `\n\n**Campaign Structure:**`
        if (campaign.adGroups && campaign.adGroups.length > 0) {
          const enabledAdGroups = campaign.adGroups.filter((ag) => ag.status === 'ENABLED').length
          campaignInfo += `\n- **Ad Groups:** ${campaign.adGroups.length} (${enabledAdGroups} enabled)`
        }
        if (campaign.ads && campaign.ads.length > 0) {
          const enabledAds = campaign.ads.filter((ad) => ad.status === 'ENABLED').length
          campaignInfo += `\n- **Ads:** ${campaign.ads.length} (${enabledAds} enabled)`

          const adTypeGroups = campaign.ads.reduce((acc: Record<string, number>, ad) => {
            const type = ad.adType || 'UNKNOWN'
            acc[type] = (acc[type] || 0) + 1
            return acc
          }, {})

          const adTypesStr = Object.entries(adTypeGroups)
            .map(([type, count]) => `${type} (${count})`)
            .join(', ')

          if (adTypesStr) {
            campaignInfo += `\n- **Ad Types:** ${adTypesStr}`
          }
        }
      }

      campaignInfo += `

**Performance Metrics:**
- **Impressions:** ${impressions.toLocaleString()}
- **Clicks:** ${clicks.toLocaleString()}
- **Cost:** $${cost.toFixed(2)}
- **CTR:** ${ctr.toFixed(2)}%
- **CPC:** $${cpc.toFixed(2)}
- **CPM:** $${cpm.toFixed(2)}
${conversions > 0 ? `- **Conversions:** ${conversions} (${conversionRate.toFixed(2)}% rate)` : ''}`

      if (campaign.targeting) {
        const targetingInfo = []
        if (campaign.targeting.locations?.length) {
          targetingInfo.push(`**Locations:** ${campaign.targeting.locations.join(', ')}`)
        }
        if (campaign.targeting.languages?.length) {
          targetingInfo.push(`**Languages:** ${campaign.targeting.languages.join(', ')}`)
        }
        if (campaign.targeting.keywords?.length) {
          targetingInfo.push(`**Keywords:** ${campaign.targeting.keywords.join(', ')}`)
        }

        if (targetingInfo.length > 0) {
          campaignInfo += `\n\n**Targeting:**\n${targetingInfo.map((info) => `- ${info}`).join('\n')}`
        }
      }

      if (campaign.dailyBreakdown?.length) {
        campaignInfo += `\n\n**Recent Daily Performance:**`
        campaign.dailyBreakdown.slice(-5).forEach((day) => {
          campaignInfo += `\n- ${day.date}: ${(day.impressions || 0).toLocaleString()} imp, ${(day.clicks || 0).toLocaleString()} clicks, $${(day.cost || 0).toFixed(2)}`
        })
      }

      return campaignInfo
    })
    .join('\n\n---\n\n')

  return `## Google Ads Campaign Details (${campaigns.length} campaigns)

${campaignsMarkdown}

---
`
}

export const GoogleAdsGetCampaignDetailsDisplay = createWorkflowToolComponent(
  GoogleAdsGetCampaignDetailsDisplayComponent,
  downloadGoogleAdsCampaignPerformance,
  googleAdsGetCampaignDetailsToMarkdown
)
